import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  flag: string;
}

export const currencies: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭' },
];

interface CurrencySelectorProps {
  value?: string;
  onValueChange: (currency: string) => void;
  className?: string;
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  value,
  onValueChange,
  className
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    return value || localStorage.getItem('preferredCurrency') || 'USD';
  });

  useEffect(() => {
    if (value && value !== selectedCurrency) {
      setSelectedCurrency(value);
    }
  }, [value, selectedCurrency]);

  const handleCurrencyChange = (currencyCode: string) => {
    setSelectedCurrency(currencyCode);
    localStorage.setItem('preferredCurrency', currencyCode);
    onValueChange(currencyCode);
  };

  const selectedCurrencyData = currencies.find(c => c.code === selectedCurrency);

  return (
    <div className={className}>
      <Label htmlFor="currency">Currency</Label>
      <Select value={selectedCurrency} onValueChange={handleCurrencyChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select currency">
            {selectedCurrencyData ? (
              <div className="flex items-center gap-2">
                <span className="text-lg">{selectedCurrencyData.flag}</span>
                <span className="font-medium">{selectedCurrencyData.symbol}</span>
                <span>{selectedCurrencyData.code}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-lg">🇺🇸</span>
                <span className="font-medium">$</span>
                <span>USD</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {currencies.map(currency => (
            <SelectItem key={currency.code} value={currency.code}>
              <div className="flex items-center gap-3">
                <span className="text-lg">{currency.flag}</span>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{currency.symbol}</span>
                    <span className="font-semibold">{currency.code}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{currency.name}</span>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export const getPreferredCurrency = (): Currency => {
  const savedCurrency = localStorage.getItem('preferredCurrency') || 'USD';
  return currencies.find(c => c.code === savedCurrency) || currencies[0];
};

export const formatCurrency = (amount: number, currencyCode: string = 'USD'): string => {
  const currency = currencies.find(c => c.code === currencyCode);
  if (!currency) return `$${amount.toFixed(2)}`;
  
  return `${currency.symbol}${amount.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};